import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  PageHeader,
  Table,
  useTable,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Tooltip,
  DateField,
  Badge,
  RefreshButton,
  Form,
  useSelect,
} from '@pankod/refine-antd'
import { ErrorComponent } from '@pankod/refine-antd'
import { useGetIdentity } from '@pankod/refine-core'
import examOnlineMetadata from 'metadata/exam_online'
import { allCandidateResultGenerating } from 'helpers/allCandidateResultGenerating'
import { getDetailsById } from 'gql/query'
import { useUserContext } from 'context/user'

type ExamOnlineIngestJob = {
  id: string
  status: string
  total_records: number
  processed_records: number
  error?: string | null
  conversion_status?: string | null
  converted_records?: number
  conversion_error?: string | null
  evaluation_id?: string | null
  created_at: string
  updated_at: string
}

const statusColor = (status?: string | null) => {
  switch (status) {
    case 'ingested':
    case 'converted':
    case 'uploaded':
      return 'green'
    case 'ingesting':
    case 'converting':
      return 'blue'
    case 'failed':
      return 'red'
    case 'queued':
      return 'gold'
    case 'skipped':
      return 'orange'
    default:
      return 'default'
  }
}

const EVALUATION_DETAIL_FIELDS = `{id candidate_id candidate_name application_id total_score evaluation_id school_id skt_id is_attended is_eligible_for_result}`

const ALLOWED_ROLES = ['admin', 'productmanager', 'deliverymanager']

const parseBackendResponse = async (response: Response) => {
  const text = await response.text()
  try {
    return { ok: response.ok, status: response.status, data: JSON.parse(text) as Record<string, unknown> }
  } catch {
    const isHtml = text.trimStart().startsWith('<!')
    throw new Error(
      isHtml
        ? `Convert API not found (HTTP ${response.status}). Deploy the latest backend or point REACT_APP_BACKEND_URL to http://localhost:3001/v1/backend for local testing.`
        : `Invalid backend response (HTTP ${response.status})`
    )
  }
}

export const ExamOnlineListPage = () => {
  const { data: user, isLoading: identityLoading } = useGetIdentity<{ id: string; defaultRole?: string }>()
  const { userRole } = useUserContext()
  // The context role is seeded by an effect in the header, so it is still the
  // 'user' default on the first render of a hard refresh at /exam-online.
  // Prefer the identity, which is the same source the header reads from.
  const effectiveRole = user?.defaultRole ?? userRole
  const canViewExamOnline = ALLOWED_ROLES.includes(effectiveRole)
  const [convertingJobIds, setConvertingJobIds] = useState<Set<string>>(new Set())
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<ExamOnlineIngestJob | null>(null)
  const [selectedEvaluationRequestId, setSelectedEvaluationRequestId] = useState<string | undefined>()
  const [convertLoading, setConvertLoading] = useState(false)
  const [jobPaperId, setJobPaperId] = useState<string | null>(null)
  const [jobPaperIdInt, setJobPaperIdInt] = useState<number | null>(null)
  const [paperInfoLoading, setPaperInfoLoading] = useState(false)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { queryResult: evaluationRequestQueryResult } = useSelect({
    resource: 'evaluation_requests',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    metaData: {
      fields: ['id', 'name', 'request_id', 'paper_id', 'created_at', { paper: ['paper_id', 'paper_smart_id'] }],
    },
    filters: jobPaperId ? [{ field: 'paper_id', operator: 'eq', value: jobPaperId }] : [],
    queryOptions: {
      enabled: canViewExamOnline && convertModalOpen && selectedJob?.conversion_status !== 'uploaded',
    },
    sort: [{ field: 'created_at', order: 'desc' }],
  })

  const { tableProps, tableQueryResult } = useTable<ExamOnlineIngestJob>({
    resource: 'exam_online_ingest_jobs',
    metaData: examOnlineMetadata,
    initialSorter: [{ field: 'created_at', order: 'desc' }],
    // Hooks run before the role guard below returns, so without this the query
    // fires for roles Hasura denies the table to.
    queryOptions: { enabled: canViewExamOnline },
  })

  const jobs = (tableProps?.dataSource || []) as ExamOnlineIngestJob[]
  const isPolling = convertingJobIds.size > 0

  const refreshTable = useCallback(() => {
    tableQueryResult.refetch()
  }, [tableQueryResult])

  useEffect(() => {
    if (!isPolling) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
      return
    }

    pollTimerRef.current = setInterval(() => {
      refreshTable()
    }, 5000)

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [isPolling, refreshTable])

  const matchedEvaluationRequests = evaluationRequestQueryResult?.data?.data ?? []
  const evaluationRequestsLoading = paperInfoLoading || evaluationRequestQueryResult?.isLoading
  const latestEvaluationRequest = matchedEvaluationRequests[0] as
    | { id?: string; name?: string; request_id?: string; created_at?: string }
    | undefined
  const latestEvaluationRequestId = latestEvaluationRequest?.id ? String(latestEvaluationRequest.id) : undefined
  const needsEvaluationRequestSelection = selectedJob?.conversion_status !== 'uploaded'

  useEffect(() => {
    if (!convertModalOpen || !needsEvaluationRequestSelection) return
    if (evaluationRequestsLoading || !latestEvaluationRequestId) return

    setSelectedEvaluationRequestId(latestEvaluationRequestId)
  }, [
    convertModalOpen,
    needsEvaluationRequestSelection,
    evaluationRequestsLoading,
    latestEvaluationRequestId,
  ])

  const runGenerateResult = async (evaluationId: string) => {
    const evaluationDetails = await getDetailsById(
      evaluationId,
      'evaluation_details',
      'evaluation_id',
      EVALUATION_DETAIL_FIELDS
    )

    if (!Array.isArray(evaluationDetails) || evaluationDetails.length === 0) {
      throw new Error('No evaluation details found for scorecard generation')
    }

    return allCandidateResultGenerating({ data: evaluationDetails })
  }

  const finalizeConversion = async (
    jobId: string,
    addedCount: number,
    skippedCount: number,
    errorMessage: string | null,
    skippedOnly = false
  ) => {
    const finishResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/exam-online/convert-finish/${jobId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          added_count: addedCount,
          skipped_count: skippedCount,
          error_message: errorMessage,
          skipped_only: skippedOnly,
        }),
      }
    )
    const finishResult = await parseBackendResponse(finishResponse)
    if (!finishResult.ok) {
      throw new Error(String(finishResult.data?.message || 'Failed to finalize conversion'))
    }
  }

  const handleConvertConfirm = async () => {
    if (!selectedJob) return

    setConvertLoading(true)
    setConvertingJobIds(prev => new Set(prev).add(selectedJob.id))

    try {
      let evaluationId = selectedJob.evaluation_id || null

      if (selectedJob.conversion_status !== 'uploaded') {
        if (!selectedEvaluationRequestId) {
          message.error('No evaluation request found for this paper')
          setConvertLoading(false)
          setConvertingJobIds(prev => {
            const next = new Set(prev)
            next.delete(selectedJob.id)
            return next
          })
          return
        }

        const uploadResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/exam-online/convert/${selectedJob.id}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              evaluation_request_id: selectedEvaluationRequestId,
              created_by: user?.id,
            }),
          }
        )
        const uploadResult = await parseBackendResponse(uploadResponse)
        const uploadPayload = uploadResult.data

        if (!uploadResult.ok || !uploadPayload?.status) {
          throw new Error(String(uploadPayload?.message || 'Failed to upload evaluation data'))
        }

        evaluationId = (uploadPayload?.evaluation_id as string | undefined) ?? null

        if (!evaluationId) {
          const skippedApplications = uploadPayload?.skipped_applications
          const skippedCount = Array.isArray(skippedApplications) ? skippedApplications.length : 0
          await finalizeConversion(selectedJob.id, 0, skippedCount, null, true)
          message.info(String(uploadPayload?.message || 'All candidates were already uploaded'))
          setConvertModalOpen(false)
          refreshTable()
          return
        }
      }

      if (!evaluationId) {
        throw new Error('Missing evaluation id for scorecard generation')
      }

      const generateResult = await runGenerateResult(evaluationId)

      if (!generateResult?.status) {
        await finalizeConversion(selectedJob.id, 0, 0, generateResult?.message || 'Generate Result failed')
        throw new Error(generateResult?.message || 'Generate Result failed')
      }

      const added = generateResult?.response?.addedCandidateResults || 0
      const existing = generateResult?.response?.alreadyExisting || 0

      await finalizeConversion(selectedJob.id, added, existing, null)

      message.success(
        `Conversion complete. Added ${added} scorecard(s), ${existing} already existed.`
      )
      setConvertModalOpen(false)
      refreshTable()
    } catch (err: any) {
      message.error(err?.message || 'Conversion failed')
      refreshTable()
    } finally {
      setConvertLoading(false)
      setConvertingJobIds(prev => {
        const next = new Set(prev)
        if (selectedJob) next.delete(selectedJob.id)
        return next
      })
    }
  }

  const openConvertModal = async (job: ExamOnlineIngestJob) => {
    setSelectedJob(job)
    setSelectedEvaluationRequestId(undefined)
    setJobPaperId(null)
    setJobPaperIdInt(null)
    setConvertModalOpen(true)

    if (job.conversion_status === 'uploaded') return

    setPaperInfoLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/exam-online/jobs/${job.id}/paper`)
      const { ok, data: payload } = await parseBackendResponse(response)
      if (ok && payload?.status && payload?.paper_id) {
        setJobPaperId(String(payload.paper_id))
        setJobPaperIdInt((payload.paper_id_int as number | null | undefined) ?? null)
      }
    } catch {
      message.warning('Could not detect paper for this job. Showing all evaluation requests.')
    } finally {
      setPaperInfoLoading(false)
    }
  }

  const canConvert = (job: ExamOnlineIngestJob) =>
    job.status === 'ingested' &&
    job.conversion_status !== 'converted' &&
    job.conversion_status !== 'converting' &&
    !convertingJobIds.has(job.id)

  const convertButtonLabel = (job: ExamOnlineIngestJob) =>
    job.conversion_status === 'uploaded' ? 'Generate Scorecards' : 'Convert'

  const modalOkText =
    selectedJob?.conversion_status === 'uploaded' ? 'Generate Scorecards' : 'Convert'

  const modalTitle =
    selectedJob?.conversion_status === 'uploaded'
      ? 'Generate Scorecards'
      : 'Convert Exam Online Results'

  if (identityLoading) {
    return null
  }

  if (!canViewExamOnline) {
    return <ErrorComponent />
  }

  return (
    <PageHeader
      title={
        <Space>
          Exam Online Results
          <Badge count={jobs.length} style={{ backgroundColor: '#722ed1' }} overflowCount={99999} />
        </Space>
      }
      extra={
        <Space>
          <RefreshButton onClick={refreshTable} />
        </Space>
      }
    >
      <Table {...tableProps} rowKey="id" bordered size="small" scroll={{ x: true }}>
        <Table.Column
          title="Job ID"
          dataIndex="id"
          render={(value: string) => (
            <Tooltip title={value}>
              <span>{value.slice(0, 8)}...</span>
            </Tooltip>
          )}
        />
        <Table.Column
          title="Ingest Status"
          dataIndex="status"
          render={(value: string) => <Tag color={statusColor(value)}>{value}</Tag>}
        />
        <Table.Column title="Total Records" dataIndex="total_records" />
        <Table.Column title="Processed Records" dataIndex="processed_records" />
        <Table.Column
          title="Conversion Status"
          dataIndex="conversion_status"
          render={(value: string | null) =>
            value ? <Tag color={statusColor(value)}>{value}</Tag> : <Tag>pending</Tag>
          }
        />
        <Table.Column title="Converted Records" dataIndex="converted_records" />
        <Table.Column
          title="Evaluation ID"
          dataIndex="evaluation_id"
          render={(value: string | null) =>
            value ? (
              <Tooltip title={value}>
                <span>{value.slice(0, 8)}...</span>
              </Tooltip>
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          title="Created At"
          dataIndex="created_at"
          render={(value: string) => <DateField value={value} format="LLL" />}
        />
        <Table.Column
          title="Updated At"
          dataIndex="updated_at"
          render={(value: string) => <DateField value={value} format="LLL" />}
        />
        <Table.Column
          title="Ingest Error"
          dataIndex="error"
          render={(value: string | null) =>
            value ? (
              <Tooltip title={value}>
                <span style={{ color: '#cf1322' }}>
                  {value.slice(0, 40)}
                  {value.length > 40 ? '...' : ''}
                </span>
              </Tooltip>
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          title="Conversion Error"
          dataIndex="conversion_error"
          render={(value: string | null) =>
            value ? (
              <Tooltip title={value}>
                <span style={{ color: '#cf1322' }}>
                  {value.slice(0, 40)}
                  {value.length > 40 ? '...' : ''}
                </span>
              </Tooltip>
            ) : (
              '-'
            )
          }
        />
        <Table.Column
          title="Actions"
          key="actions"
          fixed="right"
          render={(_: unknown, record: ExamOnlineIngestJob) => (
            <Button
              type="primary"
              size="small"
              disabled={!canConvert(record)}
              loading={convertingJobIds.has(record.id) || record.conversion_status === 'converting'}
              onClick={() => openConvertModal(record)}
            >
              {convertButtonLabel(record)}
            </Button>
          )}
        />
      </Table>

      <Modal
        title={modalTitle}
        visible={convertModalOpen}
        onCancel={() => !convertLoading && setConvertModalOpen(false)}
        onOk={handleConvertConfirm}
        okText={modalOkText}
        confirmLoading={convertLoading}
        okButtonProps={{
          disabled:
            convertLoading ||
            (needsEvaluationRequestSelection &&
              !evaluationRequestsLoading &&
              !selectedEvaluationRequestId),
        }}
        destroyOnClose
      >
        <p>
          This uses the same pipeline as manual upload: evaluation CSV upload, then Generate Result.
          Scorecards will match manual processing for the selected evaluation request.
        </p>
        {selectedJob ? (
          <p>
            <strong>{selectedJob.total_records}</strong> candidate record(s) in this job.
          </p>
        ) : null}
        {jobPaperIdInt ? (
          <p>
            Detected paper: <strong>{jobPaperIdInt}</strong>
          </p>
        ) : null}
        <Form layout="vertical">
          {needsEvaluationRequestSelection ? (
            evaluationRequestsLoading ? (
              <p>Loading evaluation requests...</p>
            ) : latestEvaluationRequest ? (
              <p>
                Evaluation request (latest for this paper):{' '}
                <strong>{latestEvaluationRequest.name || latestEvaluationRequest.request_id}</strong>
                {matchedEvaluationRequests.length > 1 ? (
                  <span style={{ color: '#595959' }}>
                    {' '}
                    ({matchedEvaluationRequests.length} requests found; using most recent)
                  </span>
                ) : null}
              </p>
            ) : (
              <p style={{ color: '#cf1322' }}>
                No evaluation requests found
                {jobPaperIdInt ? ` for paper ${jobPaperIdInt}` : ''}. Create one before converting.
              </p>
            )
          ) : (
            <p>Upload completed. Click confirm to run Generate Result for the existing evaluation.</p>
          )}
        </Form>
      </Modal>
    </PageHeader>
  )
}
